import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Layers,
  Search,
  X,
  Maximize,
  Eye,
  EyeOff,
  Building2,
  FileText,
  Map as MapIcon,
  Settings2,
  RefreshCw,
} from 'lucide-react';

interface MapToolbarProps {
  showClusters: boolean;
  showContratos: boolean;
  showGeometries: boolean;
  searchQuery: string;
  clustersCount: number;
  contratosCount: number;
  isAdmin: boolean;
  showDiagnostic: boolean;
  onToggleClusters: () => void;
  onToggleContratos: () => void;
  onToggleGeometries: () => void;
  onSearchChange: (q: string) => void;
  onZoomToExtent: () => void;
  onToggleDiagnostic: () => void;
  onRefresh: () => void;
}

const MapToolbar: React.FC<MapToolbarProps> = ({
  showClusters,
  showContratos,
  showGeometries,
  searchQuery,
  clustersCount,
  contratosCount,
  isAdmin,
  showDiagnostic,
  onToggleClusters,
  onToggleContratos,
  onToggleGeometries,
  onSearchChange,
  onZoomToExtent,
  onToggleDiagnostic,
  onRefresh,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
      {/* Main toolbar */}
      <div className="bg-card/85 backdrop-blur-xl border border-border/60 rounded-xl shadow-lg overflow-hidden">
        {/* Compact buttons row */}
        <div className="flex items-center gap-0.5 p-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg"
            onClick={() => setExpanded(!expanded)}
            title="Camadas"
          >
            <Layers className="w-4 h-4" />
          </Button>

          <div className="w-px h-5 bg-border/50" />

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg"
            onClick={onZoomToExtent}
            title="Ajustar visão"
          >
            <Maximize className="w-4 h-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg"
            onClick={onRefresh}
            title="Atualizar dados"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>

          {isAdmin && (
            <>
              <div className="w-px h-5 bg-border/50" />
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 rounded-lg ${showDiagnostic ? 'bg-primary/10 text-primary' : ''}`}
                onClick={onToggleDiagnostic}
                title="Diagnóstico"
              >
                <Settings2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>

        {/* Expandable layer panel */}
        {expanded && (
          <div className="border-t border-border/40 p-2 space-y-1">
            {/* Search */}
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar clusters ou contratos..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-8 h-8 text-xs bg-muted/30 border-border/30"
              />
              {searchQuery && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Layer toggles */}
            <LayerToggle
              icon={<MapIcon className="w-3.5 h-3.5" />}
              label="Territórios"
              active={showGeometries}
              onClick={onToggleGeometries}
            />
            <LayerToggle
              icon={<Building2 className="w-3.5 h-3.5" />}
              label={`Clusters (${clustersCount})`}
              active={showClusters}
              onClick={onToggleClusters}
            />
            <LayerToggle
              icon={<FileText className="w-3.5 h-3.5" />}
              label={`Contratos (${contratosCount})`}
              active={showContratos}
              onClick={onToggleContratos}
            />
          </div>
        )}
      </div>
    </div>
  );
};

function LayerToggle({ icon, label, active, onClick }: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-xs transition-all
        ${active ? 'text-foreground bg-primary/8 hover:bg-primary/12' : 'text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/30'}`}
    >
      {active ? <Eye className="w-3.5 h-3.5 text-primary" /> : <EyeOff className="w-3.5 h-3.5" />}
      {icon}
      <span className="font-medium">{label}</span>
    </button>
  );
}

export default MapToolbar;
